import React, { useCallback, useEffect } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Text from 'core/elements/Text'
import withFormContext, { PropsWithFormContext } from './withFormContext'
import generateTestId from 'utils/test-helpers'
import clsx from 'clsx'
import HintTooltip from 'core/elements/tooltip/HintTooltip'

interface RepeatedFieldProps<T> {
  id: string
  label: string
  value: T[]
  onChange: (value: T[]) => void
  renderItem: (item: T, index: number, updateItemCallback: (newValue: T) => void) => React.ReactNode
  addButtonText?: string
  info?: string
}

const RepeatedField = <T,>({
  id,
  label,
  value,
  onChange,
  renderItem,
  addButtonText = 'Add item',
  info,
  errorMessage,
  required,
}: PropsWithFormContext<T, RepeatedFieldProps<T>>) => {
  const classes = useStyles()
  const [fieldValues, setFieldValues] = React.useState(value || ['' as T])

  const addNewItem = useCallback((): void => {
    if (!fieldValues?.length) {
      onChange(['' as T])
      return
    }
    onChange([...fieldValues, '' as T])
  }, [fieldValues, onChange])

  const removeItem = useCallback(
    (index: number) => {
      onChange(fieldValues?.filter((_, i) => i !== index))
    },
    [fieldValues, onChange],
  )

  const updateItem = useCallback(
    (index: number, newValue: T) => {
      onChange(fieldValues?.map((item, i) => (i === index ? newValue : item)))
    },
    [fieldValues, onChange],
  )

  useEffect(() => {
    setFieldValues(value)
  }, [value])

  return (
    <div className={classes.container}>
      <div className={classes.fieldLabel}>
        <Text variant="caption1">
          {label} {required && '*'}
        </Text>
        {info && <HintTooltip message={info} />}
      </div>
      <div className={classes.list}>
        {fieldValues?.map((item, index) => (
          <div key={`${id}_${index}`} className={classes.inputRow}>
            {renderItem(item, index, (newValue: T) => updateItem(index, newValue))}
            <FontAwesomeIcon className={classes.minus} onClick={() => removeItem(index)}>
              minus-circle
            </FontAwesomeIcon>
          </div>
        ))}
        <div className={classes.addEntry}>
          <FontAwesomeIcon
            className={clsx(classes.plus, classes.clickable)}
            onClick={addNewItem}
            size="lg"
          >
            plus-circle
          </FontAwesomeIcon>
          <Text variant="body2" onClick={addNewItem} className={classes.clickable}>
            {addButtonText}
          </Text>
        </div>
      </div>
      {!!errorMessage && !fieldValues?.length && (
        <Text
          data-testid={generateTestId('repeated-field', 'error')}
          variant="body2"
          className={clsx(classes.error, 'error')}
        >
          {errorMessage}
        </Text>
      )}{' '}
    </div>
  )
}

export default withFormContext(RepeatedField)

const useStyles = makeStyles<Theme>((theme) => ({
  subnetField: {
    display: 'grid',
    gap: 8,
  },
  fieldLabel: {
    display: 'grid',
    maxWidth: 400,
    minWidth: 100,
    width: '100%',
    gridTemplateColumns: 'max-content minmax(0px, max-content)',
    gap: 8,
    alignItems: 'center',
  },
  multipleInputs: {
    display: 'grid',
    gap: 12,
  },
  inputRow: {
    display: 'grid',
    gridTemplateColumns: '356px 32px',
    gridGap: '12px',
    justifyItems: 'start',
    alignItems: 'center',
  },
  minus: {
    fontWeight: 900,
    color: theme.palette.blue.main,
    justifySelf: 'end',
    position: 'relative',
    top: 4,
  },
  addEntry: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content)',
    marginTop: 12,
    marginLeft: 8,
    alignItems: 'center',
  },
  plus: {
    color: theme.palette.blue.main,
    marginRight: 8,
    fontWeight: 900,
  },
  clickable: {
    cursor: 'pointer',
  },
  error: {
    marginLeft: 10,
    marginTop: 8,
    color: theme.components.dropdown.error,
    lineHeight: '16px',
  },
}))
